import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/api';
import { Card, Badge, Btn, Input, Select, Modal, PageHeader, fmtDate } from '../components/UI';
import RocketLoader from '../components/RocketLoader';

const TYPE_COLORS = { info: 'blue', warning: 'yellow', success: 'green', error: 'red' };
const TYPE_ICONS  = { info: 'ℹ️', warning: '⚠️', success: '✅', error: '❌' };

const AnnForm = ({ initial = {}, onSave, onClose, isNew }) => {
  const [form, setForm] = useState({
    title: '', message: '', type: 'info',
    targetAudience: 'all', active: true, expiresAt: '', ...initial,
    expiresAt: initial.expiresAt ? initial.expiresAt.substring(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setError('');
    if (!form.title || !form.message) return setError('Title and message are required');
    setSaving(true);
    try {
      await onSave({ ...form, expiresAt: form.expiresAt || null });
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? 'Create Announcement' : 'Edit Announcement'} onClose={onClose} width={540}>
      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}
      <Input label="Title" value={form.title} onChange={v => set('title', v)} placeholder="Announcement title" />
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Message</label>
        <textarea value={form.message} onChange={e => set('message', e.target.value)} placeholder="Write your announcement message..."
          style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, resize: 'vertical', minHeight: 90, outline: 'none', boxSizing: 'border-box' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Select label="Type" value={form.type} onChange={v => set('type', v)}
          options={[['info','ℹ️ Info'],['warning','⚠️ Warning'],['success','✅ Success'],['error','❌ Error']]} />
        <Select label="Target Audience" value={form.targetAudience} onChange={v => set('targetAudience', v)}
          options={[['all','All Users'],['free','Free Users Only'],['pro','Pro Users Only']]} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Expires On (optional)</label>
        <input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)}
          style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, outline: 'none', cursor: 'pointer' }} />
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', color: '#64748B', fontSize: 13, marginBottom: 20 }}>
        <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} style={{ width: 15, height: 15, accentColor: '#059669' }} />
        Active (visible to users)
      </label>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn color="ghost" onClick={onClose}>Cancel</Btn>
        <Btn color="blue" onClick={submit} disabled={saving}>{saving ? 'Saving...' : isNew ? 'Create' : 'Save Changes'}</Btn>
      </div>
    </Modal>
  );
};

export const AnnouncementsPage = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating,setCreating]= useState(false);

  const load = () => {
    setLoading(true);
    adminApi.getAnnouncements().then(setItems).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (form) => {
    const ann = await adminApi.createAnnouncement(form);
    setItems(prev => [ann, ...prev]);
  };

  const handleUpdate = async (form) => {
    const ann = await adminApi.updateAnnouncement(editing._id, form);
    setItems(prev => prev.map(a => a._id === editing._id ? ann : a));
  };

  const toggleActive = async (ann) => {
    const updated = await adminApi.updateAnnouncement(ann._id, { active: !ann.active });
    setItems(prev => prev.map(a => a._id === ann._id ? updated : a));
  };

  const del = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    await adminApi.deleteAnnouncement(id);
    setItems(prev => prev.filter(a => a._id !== id));
  };

  const bgMap = { info: '#EFF6FF', warning: '#FFFBEB', success: '#F0FDF4', error: '#FEF2F2' };
  const borderMap = { info: '#BFDBFE', warning: '#FDE68A', success: '#BBF7D0', error: '#FECACA' };

  return (
    <div>
      <PageHeader
        title="Announcements"
        subtitle="Send banners and messages to your users on the platform"
        action={<Btn color="blue" onClick={() => setCreating(true)}>+ Create Announcement</Btn>}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <RocketLoader size={140} message="Loading announcements…" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748B' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📢</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No announcements yet</div>
            <div style={{ fontSize: 13 }}>Create your first announcement to notify your users</div>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(ann => (
            <div key={ann._id} style={{ background: bgMap[ann.type] || '#FFFFFF', border: `1px solid ${borderMap[ann.type] || '#E2E8F0'}`, borderRadius: 12, padding: '16px 20px', opacity: ann.active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18 }}>{TYPE_ICONS[ann.type]}</span>
                    <span style={{ color: '#0F172A', fontWeight: 700, fontSize: 15 }}>{ann.title}</span>
                    <Badge color={TYPE_COLORS[ann.type]}>{ann.type}</Badge>
                    <Badge color={ann.active ? 'green' : 'gray'}>{ann.active ? 'Active' : 'Hidden'}</Badge>
                    <Badge color="gray">{ann.targetAudience === 'all' ? '👥 All Users' : ann.targetAudience === 'pro' ? '⭐ Pro Only' : '🆓 Free Only'}</Badge>
                  </div>
                  <p style={{ margin: '0 0 8px', color: '#64748B', fontSize: 13, lineHeight: 1.5 }}>{ann.message}</p>
                  <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#64748B' }}>
                    <span>Created: {fmtDate(ann.createdAt)}</span>
                    {ann.expiresAt && <span>Expires: {fmtDate(ann.expiresAt)}</span>}
                    {ann.createdBy && <span>By: {ann.createdBy.name}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Btn size="sm" color="ghost" onClick={() => setEditing(ann)}>Edit</Btn>
                  <Btn size="sm" color="ghost" onClick={() => toggleActive(ann)}>{ann.active ? 'Hide' : 'Show'}</Btn>
                  <Btn size="sm" color="red" onClick={() => del(ann._id)}>Delete</Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <AnnForm isNew onSave={handleCreate} onClose={() => setCreating(false)} />}
      {editing  && <AnnForm initial={editing} onSave={handleUpdate} onClose={() => setEditing(null)} />}
    </div>
  );
};
